import React, { useMemo, useState } from "react";
import { Search, X } from "lucide-react";

import { Button } from "../utils/components/ui/button";
import {
  useGetAllSalesReturn,
  useGetSalesReturnDetail,
} from "../service/salesReturnService";
import Modal from "../components/common/Modal";
import AddNewSalesReturn from "../components/forms/AddnewSalesReturn";
import PaginationComponent from "../components/PaginationComponent";
import Loading from "../components/common/LoaderCommon";

function SalesReturn() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [detailReturnId, setDetailReturnId] = useState<number | null>(null);

  const { data: detailData } = useGetSalesReturnDetail(detailReturnId);

  const {
    data: returnData,
    isLoading,
    fetchNextPage,
  } = useGetAllSalesReturn({
    pageNo: currentPage,
    pageSize: 10,
    salesReturnNumber: searchKey,
  });

  const totalPages = returnData?.totalPages || 0;
  const salesReturnRows = returnData?.salesReturnResponse || [];
  const totalElements = returnData?.totalElements || 0;

  return (
    <div className="min-h-full py-1">
      <div className="space-y-6">
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                Sales Return
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                Review returned invoices, customer-linked return records, and
                total returned value from one cleaner listing screen.
              </p>
            </div>
            <Button
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
              onClick={() => setIsModalOpen(true)}
            >
              Create Sales Return
            </Button>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-[300px] max-w-[460px] flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
              <input
                type="text"
                placeholder="Search by return invoice number"
                className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                onChange={(e) => setSearchKey(e.target.value)}
              />
            </div>
            <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998]">
              <span className="font-semibold text-[#253650]">
                {totalElements}
              </span>{" "}
              sales returns found
            </div>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#21314A]">
                Sales Return Directory
              </h2>
              <p className="mt-1 text-sm text-[#7D8998]">
                Review return number, invoice reference, customer, reason, and
                returned value from one table.
              </p>
            </div>
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
              fetchNextPage={fetchNextPage}
              fetchPreviousPage={() => {
                if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                }
              }}
            />
          </div>

          {isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-6 py-4">Return Number</th>
                    <th className="px-6 py-4">Date of Return</th>
                    <th className="px-6 py-4">Invoice No</th>
                    <th className="px-6 py-4">Customer Name</th>
                    <th className="px-6 py-4 text-right">Products Returned</th>
                    <th className="px-6 py-4 text-right">Return Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {salesReturnRows.length > 0 ? (
                    salesReturnRows.map((item: any) => (
                      <tr
                        key={item.id ?? item.invoiceReturnNumber}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                      >
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            onClick={() => setDetailReturnId(item.id)}
                            className="font-semibold text-[#1A5EF9] hover:underline cursor-pointer"
                          >
                            {item.invoiceReturnNumber}
                          </button>
                        </td>
                        <td className="px-6 py-4">{item.invoiceReturnDate}</td>
                        <td className="px-6 py-4">{item.invoiceNumber}</td>
                        <td className="px-6 py-4">
                          {item.customer?.username || "-"}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                          {item.itemCount ?? 0}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                          {item?.totalAmount?.toFixed?.(3) || "0.000"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                      >
                        No sales returns match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <Modal open={isModalOpen} setOpen={setIsModalOpen}>
          <AddNewSalesReturn setOpen={setIsModalOpen} />
        </Modal>
      )}

      {/* Return Detail Modal */}
      {detailData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-[#21314A]">
                Return Details
              </h2>
              <button
                onClick={() => setDetailReturnId(null)}
                className="text-[#52627C] hover:text-[#21314A] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-[12px] bg-[#F5F8FD] border border-[#E5ECF8] px-4 py-3">
                  <p className="text-xs text-[#7D8998]">Return Number</p>
                  <p className="text-sm font-bold text-[#1A5EF9]">
                    {detailData.invoiceReturnNumber}
                  </p>
                </div>
                <div className="rounded-[12px] bg-[#F5F8FD] border border-[#E5ECF8] px-4 py-3">
                  <p className="text-xs text-[#7D8998]">Invoice No</p>
                  <p className="text-sm font-semibold text-[#21314A]">
                    {detailData.invoiceNumber}
                  </p>
                </div>
                <div className="rounded-[12px] bg-[#F5F8FD] border border-[#E5ECF8] px-4 py-3">
                  <p className="text-xs text-[#7D8998]">Date of Return</p>
                  <p className="text-sm font-medium text-[#21314A]">
                    {detailData.invoiceReturnDate}
                  </p>
                </div>
                <div className="rounded-[12px] bg-[#F5F8FD] border border-[#E5ECF8] px-4 py-3">
                  <p className="text-xs text-[#7D8998]">Customer</p>
                  <p className="text-sm font-medium text-[#21314A]">
                    {detailData.customer?.username || "—"}
                  </p>
                </div>
              </div>

              <div className="rounded-[12px] bg-[#F5F8FD] border border-[#E5ECF8] px-4 py-3">
                <p className="text-xs text-[#7D8998]">Reason</p>
                <p className="text-sm text-[#21314A]">
                  {detailData.reason || "No reason provided"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-[12px] bg-[#F5F8FD] border border-[#E5ECF8] px-4 py-3">
                  <p className="text-xs text-[#7D8998]">Items Returned</p>
                  <p className="text-sm font-bold text-[#21314A]">
                    {detailData.itemCount ??
                      detailData.salesReturnProductsResponse?.length ??
                      0}
                  </p>
                </div>
                <div className="rounded-[12px] bg-gradient-to-r from-[#F0F4FF] to-[#F8FAFF] border border-[#BFD1FF] px-4 py-3">
                  <p className="text-xs text-[#7D8998]">Return Amount</p>
                  <p className="text-sm font-bold text-[#1A5EF9]">
                    OMR {detailData.totalAmount?.toFixed?.(3) || "0.000"}
                  </p>
                </div>
              </div>

              {/* Products list if available */}
              {detailData.salesReturnProductsResponse &&
                detailData.salesReturnProductsResponse.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#7D8998] uppercase tracking-wide mb-2">
                      Returned Products
                    </p>
                    <div className="space-y-2">
                      {detailData.salesReturnProductsResponse.map(
                        (product: any, idx: number) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-[10px] border border-[#E5ECF8] bg-white px-4 py-2.5"
                          >
                            <div>
                              <p className="text-sm font-medium text-[#21314A]">
                                {product.productName ||
                                  product.product?.productName ||
                                  "—"}
                              </p>
                              <p className="text-xs text-[#8A9BB5]">
                                Qty:{" "}
                                {product.returnQuantity || product.qty || 0}
                                {product.reason ? ` · ${product.reason}` : ""}
                              </p>
                            </div>
                            <p className="text-sm font-medium text-[#21314A]">
                              OMR {(product.totalAmount || 0).toFixed(3)}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>

            <button
              onClick={() => setDetailReturnId(null)}
              className="mt-5 h-11 w-full rounded-[12px] border border-[#D7E2F5] text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesReturn;
